import { UrlUtils } from '@streamamg/amg-common';
import { AnywhereData, AnywherePagingData, AnywhereResponse, AnywhereSectionData } from '../types/anywhere-api.type';
import { AnywhereFilters, AnywhereQueryParams } from '../types/anywhere.type';
import { CloudPayState } from '@streamamg/cloud-pay-lib/src/lib/types/cloud-pay.type';
import { ViewPermission, ViewPermissionResponse } from '../models/entitlements.model';
import { AnywhereMetaData } from '../types/anywhere-metadata.type';

export class CloudMatrixUtils {
  /**
   * Checks if there is more data available from the given paging data.
   * @param pagingData Anywhere paging data.
   */
  public static isMoreDataAvailable(pagingData: AnywherePagingData): boolean {

    if (!pagingData) {

      return false;
    }

    return ((pagingData.pageSize * pagingData.pageIndex) + pagingData.itemCount) < pagingData.totalCount;
  }

  /**
   * Adds `/search` on the end of the url if it is not present.
   * @param urlWithoutParams A clean full url without parameters.
   */
  public static addSearchEndpointToAnywhereUrl(urlWithoutParams: string): string {
    const searchIdx = urlWithoutParams.lastIndexOf('/search');

    return searchIdx === -1
      ? `${urlWithoutParams}/search`
      : urlWithoutParams;
  }

  /**
   * Extracts anywhere data from AnywhereResponse. If it contains sections, then it will get the first object within
   * that section.
   * @param data AnywhereResponse The response from anywhere
   * @see AnywhereResponse
   * @see AnywhereData
   * @see AnywhereSectionData
   */
  public static extractAnywhereData<AnywhereMetaData>(data: AnywhereResponse<AnywhereMetaData>): AnywhereData<AnywhereMetaData> {
    if ((data as AnywhereSectionData<AnywhereMetaData>).sections) {
      return (data as AnywhereSectionData<AnywhereMetaData>).sections[0];
    } else if ((data as AnywhereData<AnywhereMetaData>).itemData) {
      return (data as AnywhereData<AnywhereMetaData>);
    }

    return null;
  }

  /**
   * Generates a URL to be used for Anywhere based on the parameters. This will grab the previous URL, extracts the
   * parameters and merge them with the parameters generated from the given object if given.
   * @param anywhereUrl Anywhere URL which can also include URL parameters.
   * @param urlParameters The url parameters in key value object.
   * @param pageSize The default size of the dataset.
   * @param pageIndex The page index to retrieve.
   */
  public static generateAnywhereUrl(
    anywhereUrl: string,
    urlParameters?: AnywhereQueryParams,
    pageSize?: number,
    pageIndex?: number) {

    const urlParamsObject = {
      ...UrlUtils.extractUrlParamsFromExistingUrl(anywhereUrl),

      ...(urlParameters ? urlParameters: {}),

      pageIndex: pageIndex ? pageIndex.toString(): '0',
      pageSize: pageSize ? pageSize.toString(): '25',
    };

    const urlWithoutParams = CloudMatrixUtils
      .addSearchEndpointToAnywhereUrl(UrlUtils.removeQueryParameters(anywhereUrl));
    const urlParams = UrlUtils.generateUrlParameters(urlParamsObject);

    return `${urlWithoutParams}?${urlParams}`;
  }

  /**
   * Generates an Anywhere URL which can be used to filter by an Entry ID.
   * @param entryId Entry ID
   * @param anywhereUrl Anywhere URL which can also include URL parameters.
   * @param urlParameters The url parameters in key value object.
   */
  public static generateAnywhereUrlFilteredByEntryId(
    entryId: string,
    anywhereUrl: string,
    urlParameters?: { [key: string]: string }): string {

    const urlParamsObject = {
      ...UrlUtils.extractUrlParamsFromExistingUrl(anywhereUrl),
      ...(urlParameters ? urlParameters: {}),

      query: CloudMatrixUtils.generateAnywhereQueryFilterById(entryId),
    };

    const urlWithoutParams = CloudMatrixUtils
      .addSearchEndpointToAnywhereUrl(UrlUtils.removeQueryParameters(anywhereUrl));
    const urlParams = UrlUtils.generateUrlParameters(urlParamsObject);

    return `${urlWithoutParams}?${urlParams}`;
  }

  /**
   * Iterates through the given object which will then generate a query string for anywhere.
   * The object can be up to 2 levels where the last level will always be generated with the 'OR' operator.
   *
   * @param filters The object
   */
  public static generateSearchQueryFilters(filters: AnywhereFilters): string {
    const filtersList: string[] = [];

    Object.keys(filters).forEach((key) => {
      const filterGroup = filters[key];
      const filterGroupList: string[] = [];

      if (filterGroup?.length > 0) {

        filterGroup.forEach((filter) => {

          filterGroupList.push(`${filter.key}:(${encodeURIComponent(filter.value)})`);
        });

        filtersList.push(`(${filterGroupList.join(' OR ')})`);
      }
    });

    return filtersList.join(' AND ');
  }

  /**
   * Generates a filter string for anywhere to filter by entry ID.
   * @param entryId Kaltura entry ID
   */
  public static generateAnywhereQueryFilterById(entryId): string {
    return `(mediaData.entryId:(${entryId}))`;
  }

  /**
   * Checks if the data is given and generates a search query for anywhere.
   * @param filters The filter object to be used to generate filter.
   */
  public static generateAnywhereSearchQuery(filters: AnywhereFilters)
    : { query?: string } {
    return filters && Object.keys(filters).length > 0
      ? {query: `(${CloudMatrixUtils.generateSearchQueryFilters(filters)})`}
      : {};
  }

  /**
   * Retrieves the item data from the response.
   * @param existingAnywhereData Existing anywhere data which will be merged with data from response.
   * @param anywhereResponse The response from anywhere.
   */
  public static getItemData<AnywhereMetaData>(
    existingAnywhereData: AnywhereData<AnywhereMetaData>,
    anywhereResponse: AnywhereResponse<AnywhereMetaData>): AnywhereData<AnywhereMetaData> {

    let anywhereData: AnywhereData<AnywhereMetaData>;

    if ((anywhereResponse as AnywhereSectionData<AnywhereMetaData>).sections) {
      anywhereData = (anywhereResponse as AnywhereSectionData<AnywhereMetaData>).sections[0];
    } else if ((anywhereResponse as AnywhereData<AnywhereMetaData>).itemData) {
      anywhereData = (anywhereResponse as AnywhereData<AnywhereMetaData>);
    }

    return {
      ...anywhereData,
      itemData: (existingAnywhereData && existingAnywhereData.itemData)
        ? [
          ...existingAnywhereData.itemData,
          ...anywhereData?.itemData,
        ]
        : anywhereData?.itemData,
    };
  }

  /**
   * Retrieves the item data and generates permission object.
   * @param metaData Anywhere metadata
   * @param cloudPayState The cloudPayState object.
   * @param defaultEntitlement The default permission in config.
   */
  public static checkPermission(metaData: AnywhereMetaData, cloudPayState: CloudPayState, defaultEntitlement: ViewPermission): ViewPermissionResponse {
      if (metaData && cloudPayState) {
        if (metaData?.SysEntryEntitlements?.length > 0) {
          if (metaData.SysEntryEntitlements[0] === '*') {
            if (!cloudPayState?.isLoggedIn) {
              return {
                locked: true,
                permission: ViewPermission.Freemium,
              };
            }
            return {
              locked: false,
              permission: ViewPermission.Freemium,
            };
          } else if (metaData.SysEntryEntitlements[0] !== '?') {
            if (cloudPayState.isEntitled && cloudPayState?.entitlementsList?.length > 0) {
              const locked = !(metaData.SysEntryEntitlements.find(item => cloudPayState?.entitlementsList.includes(item)));
              return {
                locked,
                permission: ViewPermission.Premium,
              };
            }
            return {
              locked: true,
              permission: ViewPermission.Premium,
            };
          }
        }

        return {
          locked: (defaultEntitlement == ViewPermission.Freemium && !cloudPayState?.isLoggedIn) || (defaultEntitlement == ViewPermission.Premium && !cloudPayState?.isEntitled),
          permission: defaultEntitlement,
        };
      }
      return null;
    }

}
